import React, { useState, useEffect, useCallback } from "react";
import {
  makeStyles,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import CloseIcon from "@material-ui/icons/Close";
import PlayArrowRoundedIcon from "@material-ui/icons/PlayArrowRounded";
import OndemandVideoIcon from "@material-ui/icons/OndemandVideo";
import MainContainer from "../../components/MainContainer";
import { PageHeader, StatusBadge } from "../../components/ui";
import EmptyState from "../../components/EmptyState";
import { i18n } from "../../translate/i18n";
import useHelps from "../../hooks/useHelps";
import { getTokens } from "../../styles/tokens";

const useStyles = makeStyles(theme => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    scroll: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      ...theme.scrollbarStyles,
    },
    grid: {
      width: '100%',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
      gap: 16,
      paddingBottom: 16,
    },
    card: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      padding: 12,
      boxShadow: t.shadow.card,
      cursor: 'pointer',
      overflow: 'hidden',
      outline: 'none',
      transition: `border-color ${t.motion.fast}`,
      '&:hover': { borderColor: t.lineStrong },
      '&:focus-visible': { borderColor: t.accent, boxShadow: t.shadow.glow },
      '&:hover $play': { opacity: 1 },
      '&:focus-visible $play': { opacity: 1 },
    },
    cardStatic: { cursor: 'default', '&:hover': { borderColor: t.line } },
    thumb: {
      position: 'relative',
      width: '100%',
      paddingTop: '56.25%',
      borderRadius: t.radius.sm,
      backgroundColor: t.muted,
      overflow: 'hidden',
    },
    thumbImage: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      display: 'block',
    },
    // sobre a miniatura (imagem): escuro translúcido fixo, legível nos dois temas
    play: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: 44,
      height: 44,
      marginTop: -22,
      marginLeft: -22,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(13,25,53,0.72)',
      color: '#fff',
      opacity: 0.86,
      transition: `opacity ${t.motion.fast}`,
    },
    title: {
      margin: '12px 2px 0',
      fontSize: 14,
      fontWeight: 600,
      lineHeight: 1.4,
      color: t.text,
      overflowWrap: 'anywhere',
    },
    description: {
      margin: '4px 2px 2px',
      fontSize: 13,
      lineHeight: 1.5,
      color: t.textSecondary,
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      overflowWrap: 'anywhere',
    },
    emptyPaper: { boxShadow: t.shadow.card },
    videoTitleBar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      paddingRight: 12,
    },
    videoTitleText: {
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    videoContent: {
      padding: '0 !important',
    },
    videoFrame: {
      position: 'relative',
      width: '100%',
      paddingTop: '56.25%',
      backgroundColor: t.muted,
    },
    videoIframe: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      border: 0,
    },
    videoDescription: {
      margin: 0,
      padding: '14px 22px 18px',
      fontSize: 13,
      lineHeight: 1.55,
      color: t.textSecondary,
      borderTop: `1px solid ${t.line}`,
      whiteSpace: 'pre-line',
      overflowWrap: 'anywhere',
    },
  };
});

const Helps = () => {
  const classes = useStyles();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const { list } = useHelps();
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const helps = await list();
        setRecords(helps);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openVideoModal = (video) => {
    setSelectedVideo(video);
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
  };

  const handleModalClose = useCallback((event) => {
    if (event.key === "Escape") {
      closeVideoModal();
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleModalClose);
    return () => {
      document.removeEventListener("keydown", handleModalClose);
    };
  }, [handleModalClose]);

  const renderVideoModal = () => {
    const current = selectedVideo
      ? records.find((record) => record.video === selectedVideo)
      : null;
    return (
      <Dialog
        open={Boolean(selectedVideo)}
        onClose={closeVideoModal}
        maxWidth="md"
        fullWidth
        scroll="body"
      >
        <DialogTitle disableTypography className={classes.videoTitleBar}>
          <span className={classes.videoTitleText}>
            {(current && current.title) || i18n.t("helps.title")}
          </span>
          <IconButton size="small" aria-label="Fechar" onClick={closeVideoModal}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.videoContent}>
          <div className={classes.videoFrame}>
            {selectedVideo && (
              <iframe
                className={classes.videoIframe}
                src={`https://www.youtube.com/embed/${selectedVideo}`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>
          {current && current.description && (
            <p className={classes.videoDescription}>{current.description}</p>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  const renderHelps = () => {
    if (loading) {
      return (
        <div className={classes.grid}>
          {[0, 1, 2].map((key) => (
            <Paper key={key} variant="outlined" className={`${classes.card} ${classes.cardStatic}`}>
              <Skeleton variant="rect" animation="wave" className={classes.thumb} />
              <Skeleton animation="wave" height={22} width="70%" style={{ marginTop: 12 }} />
              <Skeleton animation="wave" height={18} width="90%" />
            </Paper>
          ))}
        </div>
      );
    }

    if (!records.length) {
      return (
        <Paper variant="outlined" className={classes.emptyPaper}>
          <EmptyState
            icon={<OndemandVideoIcon />}
            title="Nenhum conteúdo de ajuda por enquanto"
            message="Os vídeos e tutoriais aparecem aqui assim que forem publicados."
          />
        </Paper>
      );
    }

    return (
      <div className={classes.grid}>
        {records.map((record, key) => (
          <Paper
            key={key}
            variant="outlined"
            className={classes.card}
            role="button"
            tabIndex={0}
            aria-label={`Assistir: ${record.title || ""}`}
            onClick={() => openVideoModal(record.video)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                openVideoModal(record.video);
              }
            }}
          >
            <div className={classes.thumb}>
              <img
                src={`https://img.youtube.com/vi/${record.video}/mqdefault.jpg`}
                alt=""
                className={classes.thumbImage}
              />
              <span className={classes.play} aria-hidden="true">
                <PlayArrowRoundedIcon />
              </span>
            </div>
            <div className={classes.title}>{record.title}</div>
            {record.description && (
              <div className={classes.description}>{record.description}</div>
            )}
          </Paper>
        ))}
      </div>
    );
  };

  return (
    <MainContainer maxWidth>
      <PageHeader
        title={i18n.t("helps.title")}
        subtitle="Vídeos e tutoriais para tirar dúvidas sobre o sistema."
        badge={
          <StatusBadge color="neutral" dot={false} label={String(records.length)} />
        }
      />
      <div className={classes.scroll}>
        {renderHelps()}
      </div>
      {renderVideoModal()}
    </MainContainer>
  );
};

export default Helps;
